import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { useVisitorAlerts } from "@/components/VisitorAlerts";

const label = (t) => (t.visitor_number ? `Visitor #${t.visitor_number}` : "Visitor");

export default function VisitorInbox() {
  const alerts = useVisitorAlerts();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [visitors, setVisitors] = useState([]);
  const [threads, setThreads] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [profile, setProfile] = useState(null);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [brandFilter, setBrandFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const profileRequest = useRef(0);
  const selectedThread = useRef(null);
  const replyInFlight = useRef(false);
  const refreshSequence = useRef(0);

  useEffect(() => {
    let mounted = true;
    const refresh = async () => {
      const sequence = ++refreshSequence.current;
      try {
        const [v, t, p] = await Promise.all([
          api.get("/visitor-presence/live"), api.get("/visitor-chat/inbox"),
          api.get("/visitor-analytics/profiles"),
        ]);
        if (mounted && sequence === refreshSequence.current) { setVisitors(v.data.items || []); setThreads(t.data.items || []); setProfiles(p.data.items || []); setError(""); setLoading(false); }
      } catch { if (mounted && sequence === refreshSequence.current) { setError("Unable to load visitor activity."); setLoading(false); } }
    };
    refresh();
    const interval = setInterval(refresh, 15000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  useEffect(() => {
    selectedThread.current = selected;
    if (!selected) return;
    let mounted = true;
    setMessages([]);
    const refresh = async (markRead = false) => {
      try {
        const res = await api.get(`/visitor-chat/${selected}/messages`, markRead ? { params: { mark_read: true } } : undefined);
        if (mounted) setMessages(res.data.items || []);
        if (markRead && mounted) { setThreads(ts => ts.map(t => t.id === selected ? { ...t, unread: false } : t)); alerts.refresh(); }
      } catch { if (mounted) setError("Unable to load conversation."); }
    };
    refresh(true);
    const interval = setInterval(() => refresh(false), 5000);
    return () => { mounted = false; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const toggleRead = async (thread) => {
    const unread = !thread.unread;
    try {
      await api.post(`/visitor-chat/${thread.id}/read-state`, { unread });
      setThreads(ts => ts.map(t => t.id === thread.id ? { ...t, unread } : t));
      alerts.refresh();
    } catch { setError("Could not update conversation."); }
  };

  const send = async (event) => {
    event.preventDefault();
    if (!selected || !reply.trim() || replyInFlight.current) return;
    const threadId = selected;
    replyInFlight.current = true;
    setSending(true);
    try {
      await api.post(`/visitor-chat/${threadId}/reply`, { text: reply.trim() });
      if (selectedThread.current === threadId) setReply("");
      const res = await api.get(`/visitor-chat/${threadId}/messages`);
      if (selectedThread.current === threadId) setMessages(res.data.items || []);
    } catch { setError("Message could not be sent."); }
    finally { replyInFlight.current = false; setSending(false); }
  };

  const visibleVisitors = visitors.filter(v => brandFilter === "ALL" || v.product_code === brandFilter);
  const visibleProfiles = profiles.filter(p => brandFilter === "ALL" || p.product_code === brandFilter);
  const visibleThreads = threads.filter(t => (brandFilter === "ALL" || t.product_code === brandFilter) && (!unreadOnly || t.unread || t.id === selected));
  const selectedMeta = threads.find(t => t.id === selected);

  return (
    <main className="mx-auto max-w-3xl space-y-5 px-4 py-6" data-testid="visitor-inbox">
      <h1 className="text-xl font-bold text-slate-900">Website visitors & chat</h1>
      <p className="text-sm text-slate-500">Anonymous live visitors and conversations across your three brands.</p>
      {alerts.youOnline && <p data-testid="inbox-you-online" className="text-xs font-medium text-teal-700">You're online to website chats</p>}
      <nav aria-label="Filter by brand" className="flex flex-wrap gap-2">
        {[["ALL", "All brands"], ["CATERCTRL", "CaterCTRL"], ["VOWGENDA", "Vowgenda"], ["CENAMOS", "Cenamos"]].map(([code, label]) => (
          <button key={code} type="button" aria-pressed={brandFilter === code}
            onClick={() => { selectedThread.current = null; setBrandFilter(code); setSelected(null); setMessages([]); setProfile(null); profileRequest.current += 1; }}
            className={`rounded-full border px-3 py-2 text-sm ${brandFilter === code ? "border-teal-700 bg-teal-700 text-white" : "border-slate-200 bg-white text-slate-700"}`}>
            {label}
          </button>
        ))}
      </nav>
      {loading && <p role="status" className="text-sm text-slate-500">Loading visitor activity…</p>}
      {error && <p role="alert" className="text-sm text-rose-700">{error}</p>}
      <div className="grid grid-cols-3 gap-2" aria-label="Visitor overview">
        <div className="rounded-xl border bg-white p-3"><p className="text-xs text-slate-500">Live now</p><p className="text-2xl font-semibold">{visibleVisitors.length}</p></div>
        <div className="rounded-xl border bg-white p-3"><p className="text-xs text-slate-500">Visitor profiles</p><p className="text-2xl font-semibold">{visibleProfiles.length}</p></div>
        <div className="rounded-xl border bg-white p-3"><p className="text-xs text-slate-500">Conversations</p><p className="text-2xl font-semibold">{visibleThreads.length}</p></div>
      </div>
      <section className="rounded-xl border bg-white p-4">
        <h2 className="font-semibold">Live visitors ({visibleVisitors.length})</h2>
        {!visibleVisitors.length && <p className="mt-2 text-sm text-slate-500">No active visitors reported.</p>}
        {visibleVisitors.map(v => (
          <div key={`${v.site_domain}-${v.session_ref}`} className="border-b py-2 text-sm">
            <strong>{v.product_code}</strong> · {v.path}
            <span className="block text-xs text-slate-500">Anonymous session · {v.site_domain}</span>
          </div>
        ))}
      </section>
      <section className="rounded-xl border bg-white p-4">
        <h2 className="mb-3 font-semibold">Returning visitors</h2>
        {!visibleProfiles.length && <p className="text-sm text-slate-500">No consented visitor history yet.</p>}
        {visibleProfiles.map(p => <button key={`${p.site_domain}-${p.visitor_ref}`} type="button"
          onClick={async () => { const requestId = ++profileRequest.current; setProfile(null); try { const r = await api.get(`/visitor-analytics/profiles/${p.visitor_ref}`, { params: { site_domain: p.site_domain } }); if (profileRequest.current === requestId) setProfile(r.data); } catch { if (profileRequest.current === requestId) setError("Could not load visitor profile."); } }}
          className="block w-full border-b p-3 text-left text-sm hover:bg-slate-50">
          <strong>{p.product_code}</strong> · Anonymous {p.visitor_ref} · {p.visits} visit(s)
        </button>)}
        {profile && <div className="mt-3 rounded bg-slate-50 p-3 text-sm">
          <h3 className="font-semibold">Anonymous {profile.visitor_ref}</h3>
          <p>{profile.visits} visits · {Math.round(profile.engaged_seconds / 60)} minutes measured engagement</p>
          {(profile.pages || []).map((p, i) => <p key={i} className="mt-2">{p.site_domain}{p.path} · {p.views || 0} views · {Math.round(p.engaged_seconds / 60)} min</p>)}
          <h4 className="mt-4 font-semibold">Browsing timeline</h4>
          {(profile.timeline || []).slice(0, 50).map((e, i) => <p key={i} className="mt-1 text-xs text-slate-600">
            {new Date(e.at).toLocaleString()} · {e.site_domain}{e.path}
          </p>)}
          {profile.truncated && <p className="mt-2 text-xs text-amber-700">History limit reached; figures may be incomplete.</p>}
        </div>}
      </section>
      <section className="rounded-xl border bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="mr-auto font-semibold">Conversations</h2>
          <button type="button" data-testid="inbox-unread-filter" aria-pressed={unreadOnly} onClick={() => setUnreadOnly(v => !v)}
            className={`rounded-full border px-3 py-1.5 text-xs ${unreadOnly ? "border-teal-700 bg-teal-700 text-white" : "border-slate-200 text-slate-700"}`}>
            Unread only
          </button>
          {alerts.browserSupported && !alerts.browserEnabled && (
            <button type="button" data-testid="enable-browser-alerts-btn" onClick={alerts.enableBrowser}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-700">
              Enable browser alerts
            </button>
          )}
          {alerts.browserEnabled && <span data-testid="browser-alerts-on" className="text-xs text-teal-700">Browser alerts on</span>}
        </div>
        <div className="space-y-2">
          {visibleThreads.map(t => (
            <button key={t.id} data-testid={`conversation-${t.id}`} onClick={() => { selectedThread.current = t.id; setSelected(t.id); setMessages([]); setReply(""); }}
              className={`flex w-full items-center gap-2 rounded border p-3 text-left text-sm hover:bg-slate-50 ${t.unread ? "font-semibold" : ""}`}>
              {t.unread && <span aria-label="Unread" className="h-2 w-2 shrink-0 rounded-full bg-rose-600" />}
              <span>{label(t)} · {t.product_code} · {t.site_domain} · {t.state}</span>
              {t.offline_message && <span className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Offline message</span>}
              {t.updated_at && <span className="ml-auto shrink-0 text-xs font-normal text-slate-400">{new Date(t.updated_at).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}</span>}
            </button>
          ))}
          {!visibleThreads.length && <p className="text-sm text-slate-500">{unreadOnly ? "No unread conversations." : "No conversations yet."}</p>}
        </div>
        {selected && (
          <div className="mt-4 border-t pt-4">
            <div className="flex items-center gap-2">
              <h3 className="mr-auto font-semibold" data-testid="conversation-header">{selectedMeta ? label(selectedMeta) : "Conversation"}</h3>
              {selectedMeta?.lead_organisation_id && (
                <Link to={`/leads/${selectedMeta.lead_organisation_id}`} data-testid="conversation-lead-link"
                  className="inline-flex min-h-[44px] items-center rounded border border-teal-200 px-3 text-xs font-semibold text-teal-700">Lead created → open</Link>
              )}
              {selectedMeta && (
                <button type="button" data-testid="toggle-read-btn" onClick={() => toggleRead(selectedMeta)}
                  className="rounded border px-2 py-1 text-xs text-slate-700">
                  {selectedMeta.unread ? "Mark as read" : "Mark as unread"}
                </button>
              )}
            </div>
            <div className="mt-2 max-h-80 space-y-2 overflow-auto" aria-live="polite">
              {messages.map((m, i) => (
                <p key={i} className="rounded bg-slate-50 p-2 text-sm">
                  <strong>{m.sender === "operator" ? "Team" : "Visitor"}:</strong> {m.text}
                </p>
              ))}
            </div>
            <form onSubmit={send} className="mt-3 flex gap-2">
              <input aria-label="Reply" data-testid="inbox-reply-input" maxLength={2000} value={reply}
                onChange={e => setReply(e.target.value)}
                className="min-h-[44px] min-w-0 flex-1 rounded border p-2 text-sm" />
              <button type="submit" data-testid="inbox-reply-send" disabled={sending || !reply.trim()} className="min-h-[44px] rounded bg-teal-700 px-4 py-2 text-sm text-white disabled:opacity-50">{sending ? "Sending…" : "Reply"}</button>
            </form>
          </div>
        )}
      </section>
    </main>
  );
}
